<template>
    <div class="report-content">
        <div class="graphics">
            <Hide v-model="chartVisibility">
                <h4>{{ $t('reports.pairEvent.chartName') }}</h4>
            </Hide>

            <div v-show="chartVisibility">
                <div v-show="pairDurationLoadedAndNotEmpty">
                    <div class="average-duration"></div>
                    <div ref="averageDuration" class="chartist-legend"></div>
                </div>

                <LoadingBlock v-if="chartLoader" />
                <h4 v-if="!chartLoader && !pairDurationLoadedAndNotEmpty" class="data-empty">
                    {{ $t('common.noData') }}
                </h4>
            </div>
        </div>
        <div class="detailing">
            <Hide v-model="tableVisibility">
                <h4 ref="details">{{ $t('reports.detailed') }}</h4>
            </Hide>
        </div>
        <div v-show="tableVisibility">
            <Pagination
                v-if="reportData.length > 25"
                v-model="pagination.current_page"
                :options="pagination"
                :callback="flipThePage"
            />
            <table
                v-if="!tableLoader && reportData.length > 0"
                id="forAT_table-PairEventReport"
                class="common-table"
            >
                <tr>
                    <th>{{ $t('reporting.commonLabels.callType') }}</th>
                    <th>{{ $t('common.location') }}</th>
                    <th>{{ $t('reporting.commonLabels.startTimeDate') }}</th>
                    <th>{{ pair[0] }}</th>
                    <th>{{ pair[1] }}</th>
                    <th>{{ $t('reporting.commonLabels.endTimeDate') }}</th>
                    <th class="duration">{{ $t('reports.pairEvent.timeBetween') }}</th>
                </tr>
                <tbody>
                    <tr v-for="(call, index) in currentPage" :key="index">
                        <td>
                            <div class="call-type">
                                {{ parseCallTypes(call.call_types)[0] }}
                                <span>
                                    <i
                                        v-for="(type, index2) in parseCallTypes(call.call_types)"
                                        :key="index2"
                                        :class="'icon-' + type.toLocaleLowerCase()"
                                    ></i>
                                </span>
                            </div>
                        </td>
                        <td class="location">{{ call.location }}</td>
                        <td>
                            <div class="date">{{ call.startDateFormatted }}</div>
                            <div class="date">{{ call.startTimeFormatted }}</div>
                        </td>
                        <td>
                            <div class="date">{{ call.firstEventDateFormatted }}</div>
                            <div class="date">{{ call.firstEventTimeFormatted }}</div>
                        </td>
                        <td>
                            <div class="date">{{ call.secondEventDateFormatted }}</div>
                            <div class="date">{{ call.secondEventTimeFormatted }}</div>
                        </td>
                        <td>
                            <div v-if="!call.isUnfinished" class="date">
                                {{ call.endDateFormatted }}
                            </div>
                            <div v-if="!call.isUnfinished" class="date">
                                {{ call.endTimeFormatted }}
                            </div>
                            <div v-if="call.isUnfinished">{{ call.unfinishedText }}</div>
                        </td>
                        <td class="duration">{{ call.durationFormatted }}</td>
                    </tr>
                </tbody>
            </table>
            <LoadingBlock v-if="tableLoader" />
            <Pagination
                v-if="reportData.length > 25"
                v-model="pagination.current_page"
                :options="pagination"
                :callback="flipThePage"
            />
            <h4 v-if="!tableLoader && reportData.length === 0" class="data-empty">
                {{ $t('common.noData') }}
            </h4>
        </div>
    </div>
</template>

<script type="text/babel">
import forIn from 'lodash-es/forIn';
import some from 'lodash-es/some';
import parseISO from 'date-fns/parseISO';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import { mapState, mapActions } from 'vuex';
import Pagination from '../../common/Pagination.vue';
import Hide from '../../common/Hide.vue';
import { resourcePairEventReport, resourcePairEventDuration } from '../../../api/resources';
import { getCancelToken } from '../../../api';
import {
    formatDateString,
    getTimeString,
    doScroll,
    secsToReadableTime,
    getDateFormat,
} from '../../common/helpers';
import reportingUtils from '../../common/reportingUtils';
import { horizontalBarBuilder, isUnfinished, getUnfinishedText } from '../helpers/reportsHelper';
import isEqual from 'lodash-es/isEqual';
import LoadingBlock from '../../common/LoadingBlock';
import { ERROR, EVENT_PAIR_TYPES } from '../../../constants';

export default {
    components: {
        Pagination,
        Hide,
        LoadingBlock,
    },

    mixins: [reportingUtils],

    props: {
        reportParameters: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            reportData: [],
            pairDuration: {},
            currentPage: [],
            pagination: {
                current_page: 1,
                last_page: 0,
                per_page: 25,
            },
            chartLoader: true,
            tableLoader: true,
            chartVisibility: true,
            tableVisibility: true,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState(['callTypes']),

        pair() {
            return EVENT_PAIR_TYPES.find(p => p.value === Number(this.reportParameters.pair_type)).name.split(
                ' -> '
            );
        },

        /**
         * Average time duration chart loading status
         * @returns {Boolean}
         */
        pairDurationLoadedAndNotEmpty() {
            return (
                !this.chartLoader &&
                !!this.pairDuration &&
                some(this.pairDuration, loc => some(loc, item => item !== null))
            );
        },
    },

    watch: {
        reportParameters(newParameters, oldParameters) {
            if (!isEqual(newParameters, oldParameters)) {
                this.resetToDefault();
                this.loadReport();
            }
        },

        'pagination.current_page'() {
            doScroll(this.$refs.details);
        },

        chartVisibility(value) {
            if (value) {
                if (this.pairDuration) {
                    horizontalBarBuilder(
                        this.pairDuration,
                        this.$refs.averageDuration,
                        '.average-duration',
                        this.callTypes,
                        true
                    );
                }
            }
        },
    },

    mounted() {
        this.loadReport();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        ...mapActions({
            popup: 'popup/popup',
        }),

        /**
         * Load report data. In case of error will be displayed popup message with text.
         */
        async loadReport() {
            try {
                await Promise.all([this.loadPairDuration(), this.loadTableData()]);
            } catch (error) {
                this.tableLoader = false;
                this.chartLoader = false;
                if (error !== 'canceled' && error.data && error.data.error) {
                    this.popup({
                        type: ERROR,
                        header: this.$t('common.error'),
                        text: this.$t(error.data.error),
                    });
                }
            }
        },

        /**
         * Loading data for Average time duration chart. Initializing horizontalBarBuilder function if data is not null
         * for building chart.
         */
        async loadPairDuration() {
            let data = (
                await resourcePairEventDuration({
                    data: this.reportParameters,
                    cancelToken: this.cancelToken.token,
                })
            ).data;

            this.pairDuration = this.removeNullValuesForChartData(data);

            this.chartLoader = false;
            if (this.pairDuration) {
                horizontalBarBuilder(
                    this.pairDuration,
                    this.$refs.averageDuration,
                    '.average-duration',
                    this.callTypes,
                    true
                );
            }
        },

        /**
         * Removes all keys with empty values for given object and all null keys from values
         * @param data - object with data for pair duration chart
         * @return modified data object without null values
         */
        removeNullValuesForChartData(data) {
            let nonEmptyLocations = {};
            let sumAverage = {};
            let chartData = {};
            forIn(data, (value, key) => {
                if (some(value, item => item !== null)) {
                    nonEmptyLocations[key] = value;
                    forIn(value, (avg, cat) => {
                        sumAverage[cat] = sumAverage[cat] ? sumAverage[cat] + avg : avg;
                    });
                }
            });

            forIn(nonEmptyLocations, (value, key) => {
                forIn(sumAverage, (avg, cat) => {
                    if (avg !== null) {
                        if (!chartData[key]) {
                            chartData[key] = {};
                        }
                        chartData[key][cat] = value[cat];
                    }
                });
            });

            return chartData;
        },

        /**
         * Loading table data depends on pagination parameters. By default, the data is loaded from the 0 page.
         * @returns {Promise}
         */
        async loadTableData() {
            this.tableLoader = true;

            this.reportData = await resourcePairEventReport({
                data: this.reportParameters,
                cancelToken: this.cancelToken.token,
            }).then(({ data }) => {
                return data
                    .map(call => ({
                        ...call,
                        duration: differenceInSeconds(
                            parseISO(call.pair.second_event),
                            parseISO(call.pair.first_event)
                        ),
                    }))
                    .map(call => ({
                        ...call,
                        startDateFormatted: formatDateString(call.call_start, getDateFormat()),
                        startTimeFormatted: getTimeString(call.call_start),
                        firstEventDateFormatted: formatDateString(call.pair.first_event, getDateFormat()),
                        firstEventTimeFormatted: getTimeString(call.pair.first_event),
                        secondEventDateFormatted: formatDateString(call.pair.second_event, getDateFormat()),
                        secondEventTimeFormatted: getTimeString(call.pair.second_event),
                        endDateFormatted: formatDateString(call.call_end, getDateFormat()),
                        endTimeFormatted: getTimeString(call.call_end),
                        isUnfinished: isUnfinished(call),
                        unfinishedText: getUnfinishedText(call),
                        durationFormatted: secsToReadableTime(call.duration),
                    }));
            });

            this.pagination.last_page = Math.ceil(this.reportData.length / this.pagination.per_page);
            this.flipThePage();
            this.tableLoader = false;
        },

        flipThePage() {
            this.currentPage = this.reportData.slice(
                (this.pagination.current_page - 1) * this.pagination.per_page,
                (this.pagination.current_page - 1) * this.pagination.per_page + this.pagination.per_page
            );
        },

        resetToDefault() {
            this.reportData = [];
            this.pairDuration = {};
            this.currentPage = [];
            this.pagination = {
                current_page: 1,
                last_page: 0,
                per_page: 25,
            };
            this.chartLoader = true;
            this.tableLoader = true;
            if (this.$refs.averageDuration) {
                this.$refs.averageDuration.innerHTML = '';
            }
        },
    },
};
</script>
